/**
 * Notification Context
 * Provides app-wide notification management with iOS PWA support
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import notificationService from '../services/NotificationService';

const NotificationContext = createContext(null);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [permission, setPermission] = useState('default');
  const [isSupported, setIsSupported] = useState(true);
  const [isPWA, setIsPWA] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSPrompt, setShowIOSPrompt] = useState(false);

  // Initialize notification service
  useEffect(() => {
    const init = async () => {
      const success = await notificationService.init();
      if (success) {
        setIsInitialized(true);
        setPermission(Notification.permission);
        setIsSupported(true);
        setIsPWA(notificationService.isPWA());
        setIsIOS(notificationService.isIOSDevice());

        // Check if we should show iOS install prompt
        if (notificationService.isIOSDevice() && !notificationService.isPWA()) {
          // Check if user has dismissed the prompt before
          const dismissed = localStorage.getItem('ios_pwa_prompt_dismissed');
          if (!dismissed) {
            setShowIOSPrompt(true);
          }
        }
      } else {
        setIsSupported(false);
      }
    };

    init();
  }, []);

  // Request permission
  const requestPermission = useCallback(async () => {
    const result = await notificationService.requestPermission();
    setPermission(result);
    return result;
  }, []);

  // Show incoming call notification
  const showIncomingCall = useCallback(async (caller, callId, isVideo = true) => {
    if (permission !== 'granted') {
      await requestPermission();
    }
    return notificationService.showIncomingCall(caller, callId, isVideo);
  }, [permission, requestPermission]);

  // Show message notification
  const showMessage = useCallback(async (sender, message, chatId) => {
    if (permission !== 'granted') {
      return false;
    }
    return notificationService.showMessage(sender, message, chatId);
  }, [permission]);

  // Show task notification
  const showTask = useCallback(async (task) => {
    if (permission !== 'granted') {
      return false;
    }
    return notificationService.showTask(task);
  }, [permission]);

  // Show generic notification
  const showGeneric = useCallback(async (title, body, url = null) => {
    if (permission !== 'granted') {
      return false;
    }
    return notificationService.showGeneric(title, body, url);
  }, [permission]);

  // Dismiss iOS prompt
  const dismissIOSPrompt = useCallback(() => {
    setShowIOSPrompt(false);
    localStorage.setItem('ios_pwa_prompt_dismissed', 'true');
  }, []);

  // Reset iOS prompt (for testing)
  const resetIOSPrompt = useCallback(() => {
    localStorage.removeItem('ios_pwa_prompt_dismissed');
    if (notificationService.isIOSDevice() && !notificationService.isPWA()) {
      setShowIOSPrompt(true);
    }
  }, []);

  const value = {
    isInitialized,
    isSupported,
    permission,
    isPWA,
    isIOS,
    showIOSPrompt,
    requestPermission,
    showIncomingCall,
    showMessage,
    showTask,
    showGeneric,
    dismissIOSPrompt,
    resetIOSPrompt,
    getStatus: () => notificationService.getStatus()
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
